import { authService } from '../../../services/auth.service.ts';
import type { Whiteboard, WhiteboardElement } from '../types/whiteboard.types.ts';

const STORAGE_PREFIX = 'tbkmeet_whiteboards_';

function storageKey(): string {
  const userId = authService.getUser()?.id ?? 'guest';
  return `${STORAGE_PREFIX}${userId}`;
}

function readAll(): Whiteboard[] {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Whiteboard[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(boards: Whiteboard[]): void {
  localStorage.setItem(storageKey(), JSON.stringify(boards));
}

export async function listWhiteboards(): Promise<Whiteboard[]> {
  return readAll().sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function getWhiteboard(id: string): Promise<Whiteboard | null> {
  return readAll().find((b) => b.id === id) ?? null;
}

export async function createWhiteboard(title = 'Adsız beyaz tahta'): Promise<Whiteboard> {
  const user = authService.getUser();
  const now = Date.now();
  const board: Whiteboard = {
    id: `wb_${now}_${Math.random().toString(36).slice(2, 9)}`,
    title,
    ownerId: user?.id ?? 'guest',
    createdAt: now,
    updatedAt: now,
    starred: false,
    thumbnail: null,
    backgroundColor: '#ffffff',
    elements: [],
  };
  const boards = readAll();
  boards.unshift(board);
  writeAll(boards);
  return board;
}

export async function updateWhiteboard(
  id: string,
  patch: Partial<Pick<Whiteboard, 'title' | 'starred' | 'thumbnail' | 'backgroundColor' | 'elements'>>
): Promise<Whiteboard | null> {
  const boards = readAll();
  const index = boards.findIndex((b) => b.id === id);
  if (index < 0) return null;

  boards[index] = {
    ...boards[index],
    ...patch,
    updatedAt: Date.now(),
  };
  writeAll(boards);
  return boards[index];
}

export async function deleteWhiteboard(id: string): Promise<void> {
  writeAll(readAll().filter((b) => b.id !== id));
}

export async function duplicateWhiteboard(id: string): Promise<Whiteboard | null> {
  const source = await getWhiteboard(id);
  if (!source) return null;

  const now = Date.now();
  const copy: Whiteboard = {
    ...source,
    id: `wb_${now}_${Math.random().toString(36).slice(2, 9)}`,
    title: `${source.title} (kopya)`,
    createdAt: now,
    updatedAt: now,
    starred: false,
    elements: JSON.parse(JSON.stringify(source.elements)) as WhiteboardElement[],
  };

  const boards = readAll();
  boards.unshift(copy);
  writeAll(boards);
  return copy;
}
